import { useCallback } from 'react';

import { getRouteApi, useNavigate } from '@mint/routing';

import { CONNECTION_PATHS } from '../connectionPaths.js';
import { failConnectionAddOperation } from '../operations/connectionAddOperation.js';
import { useAbandonUnfinishedConnectionOperation } from '../operations/connectionOperation.js';
import { failConnectionReconnectOperationForOAuthResult } from '../operations/connectionReconnectOperation.js';
import useConnectionOAuthResult, {
  OAUTH_RESULTS,
} from './useConnectionOAuthResult.js';

const addRouteApi = getRouteApi(
  '/_secured/external-data/integrations/$integration/connections/add',
);

type OAuthResultSearchParams = {
  oauth_result?: string;
};

/**
 * The generic, platform-agnostic `oauth_result` handling for the add-connection
 * page: reads `oauth_result` off the add route's search params, shows a
 * translated error snackbar, and navigates back to the add page on failure
 * (success is a no-op — the backend already normalizes every platform's
 * callback into the same result enum, so there's nothing platform-specific
 * to branch on here).
 *
 * Lives here (not in `packages/connections`) so a platform package's own
 * `IntegrationDefinition` can expose it directly as
 * `connection.useAddConnectionOAuthResult`, without that package needing a
 * dependency back on the app package that assembles the registry.
 */
const useAddConnectionOAuthResult = () => {
  const searchParams = addRouteApi.useSearch() as OAuthResultSearchParams;
  const { integration: platformName } = addRouteApi.useParams();
  const navigate = useNavigate();

  const oauthResult = searchParams.oauth_result ?? '';

  useAbandonUnfinishedConnectionOperation();

  // Also ends a reconnect whose OAuth error comes back here; each end call
  // only ends the operation this tab started.
  const handleOAuthError = useCallback(() => {
    failConnectionAddOperation('error');
    failConnectionReconnectOperationForOAuthResult(oauthResult);
    void navigate({
      to: CONNECTION_PATHS.connectionsAdd,
      params: {
        integration: platformName,
      },
      replace: true,
    });
  }, [navigate, oauthResult, platformName]);

  useConnectionOAuthResult({
    oauthResult: oauthResult === OAUTH_RESULTS.success ? '' : oauthResult,
    onError: handleOAuthError,
  });
};

export default useAddConnectionOAuthResult;
